import { useState } from "react"
import { router, usePage } from "@inertiajs/react"
import { Ban } from "lucide-react"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Select } from "@/components/ui/select"
import { ChannelIcon } from "@/lib/channel-icons"
import type { CalendarChannel } from "./types"

type Props = {
  channels: CalendarChannel[]
  trigger: React.ReactNode
}

// Duration choices: 30 minutes, then whole hours up to 12 (mirrors
// CalendarBlock::DURATION_OPTIONS on the server).
const DURATION_OPTIONS: { value: number; label: string }[] = [
  { value: 30, label: "30 minutes" },
  ...Array.from({ length: 12 }, (_, i) => {
    const h = i + 1
    return { value: h * 60, label: h === 1 ? "1 hour" : `${h} hours` }
  }),
]

// Channel types a new block covers by default.
const DEFAULT_BLOCKED_TYPES = new Set(["x", "bluesky"])

export function AddBlockModal({ channels, trigger }: Props) {
  const { errors } = usePage().props as { errors?: Record<string, string> }
  const [open, setOpen] = useState(false)
  const [date, setDate] = useState<string>(todayIso())
  const [time, setTime] = useState<string>(nextHour())
  const [duration, setDuration] = useState<number>(60)
  const [selected, setSelected] = useState<Set<string>>(
    () => new Set(channels.filter((c) => DEFAULT_BLOCKED_TYPES.has(c.channel_type)).map((c) => c.slug))
  )
  const [submitting, setSubmitting] = useState(false)

  const toggleChannel = (slug: string) => {
    setSelected((prev) => {
      const next = new Set(prev)
      next.has(slug) ? next.delete(slug) : next.add(slug)
      return next
    })
  }

  const submit = (e: React.FormEvent) => {
    e.preventDefault()
    if (selected.size === 0) return

    // Interpret the picked date + time in the browser's local time, matching
    // how the rest of the calendar renders and drags slots.
    const [h, m] = time.split(":").map(Number)
    const startsAt = new Date(`${date}T00:00:00`)
    startsAt.setHours(h, m, 0, 0)

    setSubmitting(true)
    router.post(
      "/calendar/blocks",
      {
        calendar_block: {
          starts_at: startsAt.toISOString(),
          duration_minutes: duration,
          channels: Array.from(selected),
        },
      },
      {
        preserveScroll: true,
        onFinish: () => setSubmitting(false),
        onSuccess: () => setOpen(false),
      }
    )
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent size="sm">
        <DialogHeader>
          <DialogTitle>Block time</DialogTitle>
          <DialogDescription>
            Mark a window as occupied — nothing can be scheduled into it on the channels you pick
            (e.g. a post you scheduled natively on the platform).
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="mt-3 flex flex-col gap-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label htmlFor="block-date">Day</label>
              <Input
                id="block-date"
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                required
              />
            </div>
            <div>
              <label htmlFor="block-time">Start time</label>
              <Input
                id="block-time"
                type="time"
                value={time}
                onChange={(e) => setTime(e.target.value)}
                required
              />
            </div>
          </div>

          <div>
            <label htmlFor="block-duration">Duration</label>
            <Select
              id="block-duration"
              value={String(duration)}
              onChange={(e) => setDuration(Number(e.target.value))}
            >
              {DURATION_OPTIONS.map((opt) => (
                <option key={opt.value} value={String(opt.value)}>{opt.label}</option>
              ))}
            </Select>
          </div>

          <fieldset>
            <legend>Channels to block</legend>
            <div className="mt-1.5 flex flex-wrap gap-1.5">
              {channels.map((c) => (
                <button
                  key={c.slug}
                  type="button"
                  className="toggle-button toggle-button-with-icon"
                  aria-pressed={selected.has(c.slug)}
                  onClick={() => toggleChannel(c.slug)}
                >
                  <ChannelIcon channelType={c.channel_type} color={c.color} className="h-3.5 w-3.5" />
                  {c.name}
                </button>
              ))}
            </div>
            {selected.size === 0 && (
              <p className="mt-1 text-xs text-danger-display">Pick at least one channel.</p>
            )}
          </fieldset>

          {errors?.channels && <p className="text-xs text-danger-display">{errors.channels}</p>}
          {errors?.starts_at && <p className="text-xs text-danger-display">{errors.starts_at}</p>}
          {errors?.duration_minutes && <p className="text-xs text-danger-display">{errors.duration_minutes}</p>}

          <Button type="submit" disabled={submitting || selected.size === 0}>
            <Ban className="h-4 w-4" />
            {submitting ? "Blocking…" : "Block this time"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  )
}

function todayIso() {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`
}

// Default start time: the top of the next hour.
function nextHour() {
  const d = new Date()
  d.setHours(d.getHours() + 1, 0, 0, 0)
  return `${String(d.getHours()).padStart(2, "0")}:00`
}
